import { useEffect, useState } from "react";
import Button from "../design/Button";
import styles from "./OverviewTab.module.css";
import AddonUsersModal from "./Modals/AddonUsersModal";
import { useNavigate } from "react-router-dom";
import { modalCloseIcon } from "../../utility-functions/assetLoader";
import {
  calcPrice,
  formatDate,
  getOrgURLParam,
  isTrialSubscription,
  sentenceCase,
  AUTO_CANCELLED,
} from "../../utility-functions";
import ManageLicenseModal from "./Modals/ManageLicenseModal";
import { useDispatch, useSelector } from "react-redux";
import { devmode, JService } from "../api-service/ApiService";
import VirtualNumberModal from "./Modals/VirtualNumberModal";
import AddLicenseModal from "./Modals/AddLicenseModal";
import { setCredits } from "../features/account/account";
import AnimationPage from "../shared/animation-page/AnimationPage";
import { enqueueSnackbar } from "notistack";
import PaymentReminderModal from "../design/components/customComponents";
import Tag from "../whatsapp-business/Components/Tag";
import classes from "../design/components/ReactivateSubscription.module.css"

const OverviewTab = ({
  status,
  plan,
  viewers,
  addons,
  numbers,
  endDate,
  startDate,
  currency,
  changeActiveTab,
  handleUpdateAddonUsers,
  refetchData,
  paymentDueDate,
}) => {
  const API_BASE_URL = process.env.REACT_APP_BETA;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const connectedNumbers = useSelector(
    (state) => state?.whatsappBusiness?.value?.phone_numbers?.length ?? 0
  );
  const { canViewUsers, canCreateEditUsers } = useSelector(
    (state) => state?.auth?.permissions
  );
  const whatsappAPIStatus = useSelector(
    (state) => state?.whatsappBusiness?.value?.status || "PENDING"
  );
  const autodialData = addons?.find((addon) => addon.name === "AUTODIAL");
  const browserCallingData = addons?.find(
    (addon) => addon.name === "BROWSER CALLING"
  );

  //States
  const [freeNumbersCount, setFreeNumbersCount] = useState(0);
  const [upgradeBoxVisible, setUpgradeBoxVisible] = useState(false);
  const [manageLicenseModalData, setManageLicenseModalData] = useState({
    type: "",
    visible: false,
    licenses: {},
  });
  const [updateLicensePayload, setUpdateLicensePayload] = useState({
    payload: { action: "", quantity: null },
    type: "",
  });
  const [addLicenseModalVisible, setAddLicenseModalVisible] = useState({
    type: "",
    visible: false,
    featureId: null,
  });
  const [assignUsersModal, setAssignUsersModal] = useState({
    name: "",
    visible: false,
    data: null,
  });
  const [users, setUsers] = useState([]);
  const [vnModalVisible, setVnModalVisible] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [embeddedSignupValues, setEmbeddedSignupValues] = useState({
    phone_number_id: null,
    business_account_id: null,
    code: null,
  });
  const [showPaymentMethodWarning, setShowPaymentMethodWarning] =
    useState(true);
  const [applicableAddons, setApplicableAddons] = useState([]);

  //Handlers
  const handleUpgrade = () => {
    navigate('/upgrade', { state: { isAgreementCompleted: false, isKycCompleted: false } });
  };

  const handleUpgradeBoxClose = () => {
    localStorage.removeItem("account-upgraded-today");
    setUpgradeBoxVisible(false);
  };
  const handleOpenLicenseModal = (type, data = null) => {
    if (type === "Plan") {
      setManageLicenseModalData({
        type: type,
        visible: true,
        licenses: plan.licenses,
      });
    } else if (type === "Viewer") {
      if (viewers?.licenses?.total === 0)
        setAddLicenseModalVisible({ type, visible: true });
      else
        setManageLicenseModalData({
          type: type,
          visible: true,
          licenses: viewers.licenses,
        });
    } else if (data) {
      if (data?.licenses?.total === 0)
        setAddLicenseModalVisible({ type, visible: true, featureId: data?.id });
      else
        setManageLicenseModalData({
          type: type,
          visible: true,
          licenses: data?.licenses,
          featureId: data?.id,
        });
    }
  };

  const handleCloseLicenseModal = () => {
    setManageLicenseModalData({
      type: "",
      visible: false,
      licenses: {},
    });
  };
  const updateCreditsData = () => {
    JService.get(
      API_BASE_URL + `/api/v1/subscriptions/credit/${getOrgURLParam()}`
    )
      .then((res) => {
        if (!res?.success)
          throw new Error(res?.message || "Failed to retrieve credits");
        dispatch(setCredits(res?.data));
      })
      .catch((err) => {
        console.error("Error while retrieving credits:", err);
      });
  };

  const updateLicense = (
    type = updateLicensePayload?.type,
    payload = updateLicensePayload?.payload
  ) => {
    let _payload = { ...payload };
    let url = ``;

    if (type === "Viewer") {
      url = `/api/v1/subscriptions/viewer-licenses/`;
    }
    else if(type === "Plan"){
      url = `/api/v1/subscriptions/plan-licenses/`;
    }
    else {
      _payload["addon"] = type?.toUpperCase();
      url = `/api/v1/subscriptions/addon-licenses/`;
    } 

    url += `${getOrgURLParam()}`;

    setShowLoader(true);
    JService.post(url, JSON.stringify(_payload))
      .then((res) => {
        if (!res?.success) {
          throw new Error(res?.message);
        }

        handleCloseLicenseModal();
        enqueueSnackbar(res?.message || "Licenses will be updated shortly", {
          variant: "success",
        });
        setUpdateLicensePayload({
          payload: { action: "", quantity: null },
          type: "",
        });

        setTimeout(() => {
          updateCreditsData();
          refetchData();
        }, 1000);
      })
      .catch((err) => {
        enqueueSnackbar(err?.message || "Could not update licenses", {
          variant: "error",
        });
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const handleRemoveLicense = (quantity, type) => {
    setManageLicenseModalData((prev) => ({ ...prev, visible: false }));
    if (quantity <= 0) return;
    const payload = {
      action: "remove",
      quantity: Number(quantity),
    };
    setUpdateLicensePayload({ payload, type });
    if (
      Number(plan?.licenses?.total) - Number(plan?.licenses?.deleted) - Number(quantity) >= freeNumbersCount ||
      freeNumbersCount <= 0 ||
      type !== "Plan"
    ) {
      updateLicense(type, payload);
    } else {
      setVnModalVisible(true);
    }
  };

  const handleRemoveNumbers = (idsList) => {
    setVnModalVisible(false);
    const payload = {
      ...updateLicensePayload.payload,
      number_ids: [...idsList],
    };
    updateLicense("Plan", payload);
  };

  const handleVnModalClose = () => {
    setVnModalVisible(false);
    updateLicense("Plan", updateLicensePayload);
  };

  const onPayNow = () => {
    JService.get("/api/v1/subscriptions/pay-now/")
      .then((res) => {
        if (!res?.success) {
          throw new Error(res?.message || "Something went wrong");
        }
        if (res?.url) {
          window.location.href = res.url;
        }
      })
      .catch((err) => {
        enqueueSnackbar(err?.message || "Could not process payment", {
          variant: "error",
        });
      });
  };

  // Session logging message event listener
  const embeddedSignupMessageListener = (event) => {
    if (
      event.origin !== "https://www.facebook.com" &&
      event.origin !== "https://web.facebook.com"
    )
      return;

    console.log("Event listener waba called: ", event);
    try {
      const data = JSON.parse(event.data);
      if (data.type === "WA_EMBEDDED_SIGNUP") {
        if (data.event === "CANCEL") {
          console.error(
            "Embedded signup abandoned at step",
            data.data.current_step
          );
        } else if (data.event === "FINISH") {
          setEmbeddedSignupValues((prev) => ({
            ...prev,
            phone_number_id: data.data.phone_number_id,
            business_account_id: data.data.waba_id,
          }));
        }
      }
    } catch {
      console.error("Error in embedded signup flow: ", event.data);
    }
  };

  // Response callback
  const fbLoginCallback = (response) => {
    if (response.authResponse) {
      const code = response.authResponse.code;
      setEmbeddedSignupValues((prev) => ({ ...prev, code: code }));
    } else {
      console.error("Error in completing embedded signup flow: ", response);
    }
  };

  // Launch method and callback registration
  const launchWhatsAppSignup = () => {
    const config_id = devmode
      ? process.env.REACT_APP_WHATSAPP_BUSINESS_CONFIGURATION_ID_DEV
      : process.env.REACT_APP_WHATSAPP_BUSINESS_CONFIGURATION_ID;
    window.FB.login(fbLoginCallback, {
      config_id,
      response_type: "code",
      override_default_response_type: true,
      extras: {
        setup: {},
        featureType: "",
        sessionInfoVersion: "3",
      },
    });
  };

  const postEmbeddedSignup = async () => {
    try {
      const response = await JService.post(
        `/api/v1/whatsapp/embedded-signup/`,
        embeddedSignupValues
      );
      if (!response?.success) {
        throw new Error(response?.message);
      }
      enqueueSnackbar(
        response?.message || "Application submitted successfully",
        { variant: "success" }
      );
    } catch (error) {
      if (error?.message) {
        enqueueSnackbar(
          error?.message ||
            "There was some error during signup process. Please try again.",
          { variant: "error" }
        );
      }
    } finally {
      window.location.reload();
    }
  };

  //Effects

  useEffect(() => {
    if(!numbers || !Array.isArray(numbers)){
      return;
    }
    let freeNums = 0;
    numbers.forEach(num => {
      if(num.is_included_in_plan) {
        freeNums += num.total;
      }
    })
    setFreeNumbersCount(freeNums)
  }, [numbers]);


  useEffect(() => {
    window.addEventListener("message", embeddedSignupMessageListener);

    return () =>
      window.removeEventListener("message", embeddedSignupMessageListener);
  }, [embeddedSignupValues]);

  useEffect(() => {
    if (
      embeddedSignupValues?.business_account_id &&
      embeddedSignupValues?.code &&
      embeddedSignupValues?.phone_number_id
    ) {
      postEmbeddedSignup();
    }
  }, [embeddedSignupValues]);

  useEffect(() => {
    if (
      status === "ACTIVE" &&
      JSON.parse(localStorage.getItem("account-upgraded-today")) == true
    ) {
      if (Date.now() - new Date(startDate) < 86400000) {
        setUpgradeBoxVisible(true);
      } else {
        localStorage.removeItem("account-upgraded-today");
      }
    }
  }, [status, startDate]);

  useEffect(() => {
    const controller = new AbortController();
    if (canViewUsers) {
      if (!showLoader) setShowLoader(true);
      JService.get(
        `/api/v1/auth/retrieve-users/${getOrgURLParam()}`,
        {},
        { signal: controller.signal }
      )
        .then((res) => {
          if (!("success" in res)) return;
          if (!res?.success)
            throw new Error(res?.message || "Could not retreive users");
          setShowLoader(false);
          setUsers(res?.data);
        })
        .catch((err) => {
          setShowLoader(false);
        });
    }

    return () => {
      controller.abort();
      setUsers([]);
    };
  }, [canViewUsers]);

  useEffect(() => {
    JService.get(`/api/v1/subscriptions/retrieve-applicable-addons/${getOrgURLParam()}`)
      .then((res) => {
        if(!res?.success) throw new Error(res?.message);
        setApplicableAddons(res?.data ?? []);
      })
      .catch((err) => {
        console.error("Error retrieving applicable addons:", err);
        enqueueSnackbar(err?.message || "Could not retreive applicable addons", {
          variant: "error",
        });
      });
  }, []);

  if (isTrialSubscription(status)) {
    return (
      <div className={styles.container}>
        <section>
          <h2 className="t5 medium-font">Plans & Numbers</h2>
          <OverviewTable
            heading="Professional Plan - Trial"
            buttons={[
              <Button
                buttonText="Upgrade"
                onClick={handleUpgrade}
                hierarchy="green"
                disabled={false}
                key={1}
              />,
            ]}
            rows={[
              {
                data: `${plan?.licenses?.total} License${plan?.licenses?.total > 1 ? "s" : ""}`,
              },
            ]}
          />
          <OverviewTable
            heading="Numbers - Trial"
            rows={
              numbers?.length === 0
                ? [{ data: <p>No numbers added to account</p> }]
                : numbers?.map((elem) => {
                    return {
                      key: elem,
                      data: (
                        <p className="t7 regular-font">
                          {elem?.total}{" "}{elem?.location} number x {currency}{elem?.price} per month
                        </p>
                      ),
                    };
                  })
            }
          />
        </section>
        <hr />
        <section>
          <h2 className="t5 medium-font">Add-ons</h2>
          <OverviewTable
            heading="Autodial - Trial"
            rows={[
              {
                data: `${autodialData?.licenses?.total} License${autodialData?.licenses?.total > 1 ? "s" : ""}`,
              },
            ]}
          />
          {plan?.name?.includes("INR") && (
            <OverviewTable
              heading="Browser calling - Trial"
              rows={[
                {
                  data: `${browserCallingData?.licenses?.total} License${browserCallingData?.licenses?.total > 1 ? "s" : ""}`,
                },
              ]}
            />
          )}
        </section>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {endDate > Date.now() && Date.now() <= paymentDueDate && (
        <PaymentReminderModal
          planName={plan?.name}
          dueDate={endDate}
          onPayNow={onPayNow}
        />
      )}
      <AddonUsersModal
        visible={assignUsersModal?.visible}
        handleClose={() =>
          setAssignUsersModal((prev) => ({
            ...prev,
            visible: false,
            data: null,
            name: "",
          }))
        }
        data={assignUsersModal?.data}
        users={users}
        handleUpdateAddonUsers={handleUpdateAddonUsers}
        addonName={assignUsersModal?.name}
      />
      <ManageLicenseModal
        handleRemoveLicense={handleRemoveLicense}
        visible={manageLicenseModalData?.visible}
        handleClose={handleCloseLicenseModal}
        data={manageLicenseModalData}
        renewalDate={endDate}
      />
      <AddLicenseModal
        visible={addLicenseModalVisible.visible}
        handleClose={() =>
          setAddLicenseModalVisible({
            type: "",
            visible: false,
            featureId: null,
          })
        }
        type={addLicenseModalVisible.type}
        featureId={addLicenseModalVisible.featureId}
      />
      <VirtualNumberModal
        visible={vnModalVisible}
        handleClose={handleVnModalClose}
        handleRemoveNumbers={handleRemoveNumbers}
        renewalDate={endDate}
      />

      {upgradeBoxVisible && (
        <div className={styles.freeVnLicenseInfo}>
          <div>
            <p>Thank you for choosing FreJun 🙌</p>
            <span onClick={handleUpgradeBoxClose}>
              {modalCloseIcon("#3F3F46")}
            </span>
          </div>
          <p>
            Your plan includes 1 free virtual number per license. Get yours now!
          </p>
          <Button
            variant="filled"
            buttonText="Get virtual number"
            hierarchy="green"
            onClick={() => navigate("/virtual-numbers")}
          />
        </div>
      )}
      {showLoader && <AnimationPage />}
      {!showLoader && (
        <>
          {endDate && status === "ACTIVE" && (
            <div className={styles.renewalInfo}>
              <p className="t7 regular-font">
                Your next invoice will be issued on{" "}
                <span className={styles.renewDate}>{formatDate(endDate)}</span>
              </p>
            </div>
          )}
          <section>
            <h2 className="t5 medium-font">Plans & Numbers</h2>
            <OverviewTable
              total={plan?.licenses?.total}
              heading={`${plan?.name?.includes("Professional") ? "Professional Plan" : "Standard Plan"}`}
              buttons={[
                status === AUTO_CANCELLED ? (
                  <button
                    className={classes.reactivateButton}
                    onClick={() =>
                      navigate("/billing/cart", {
                        state: { from: "reactivate" },
                      })
                    }
                  >
                    Reactivate
                  </button>
                ) : (
                  <Button
                    key={2}
                    variant="outlined"
                    styleOverride={{
                      backgroundColor: "#fff",
                      fontSize: "14px",
                    }}
                    buttonText="Manage License"
                    onClick={() => handleOpenLicenseModal("Plan")}
                    hierarchy="white"
                    disabled={false}
                  />
                ),
              ]}
              renewDate={endDate}
              rows={[
                {
                  data: (
                    <p>
                      <span className="medium-font">
                        {plan?.licenses?.total}
                      </span>{" "}
                      License{plan?.licenses?.total > 1 ? "s" : ""} x{" "}
                      {Number(plan?.price) > Number(plan?.discounted_price) && (
                        <span style={{ textDecoration: "line-through" }}>
                          {currency}
                          {calcPrice(plan)[0]}
                        </span>
                      )}
                      <span className="medium-font">
                        {" "}
                        {currency}
                        {calcPrice(plan)[1]}
                      </span>{" "}
                      per month
                    </p>
                  ),
                },
                {
                  data: (
                    <p>
                      <span className="medium-font">
                        {plan?.licenses?.total - plan?.licenses?.free}
                      </span>{" "}
                      License{plan?.licenses?.total > 1 ? "s" : ""} assigned
                    </p>
                  ),
                  buttonText: "View",
                  btnClick: () => navigate("/users-and-teams"),
                },
              ]}
            />

            <OverviewTable
              total={viewers?.licenses?.total}
              heading="Viewer License"
              buttons={[
                status !== AUTO_CANCELLED && 
                <Button
                  key={2}
                  variant="outlined"
                  styleOverride={{ backgroundColor: "#fff", fontSize: "14px" }}
                  buttonText={
                    viewers?.licenses?.total <= 0
                      ? "Add License"
                      : "Manage license"
                  }
                  onClick={() => handleOpenLicenseModal("Viewer")}
                  hierarchy="white"
                  disabled={false}
                />,
              ]}
              renewDate={endDate}
              rows={[
                {
                  data: (
                    <p>
                      <span className="medium-font">
                        {viewers?.licenses?.total}
                      </span>{" "}
                      License{viewers?.licenses?.total > 1 ? "s" : ""} x{" "}
                      {Number(viewers?.price) >
                        Number(viewers?.discounted_price) && (
                        <span style={{ textDecoration: "line-through" }}>
                          {currency}
                          {viewers?.price}
                        </span>
                      )}
                      <span className="medium-font">
                        {" "}
                        {currency}
                        {viewers?.discounted_price}
                      </span>{" "}
                      per month
                    </p>
                  ),
                },
                {
                  data: (
                    <p>
                      <span className="medium-font">
                        {viewers?.licenses?.total - viewers?.licenses?.free}
                      </span>{" "}
                      License{viewers?.licenses?.total > 1 ? "s" : ""} assigned
                    </p>
                  ),
                  buttonText: "View",
                  btnClick: () => navigate("/users-and-teams"),
                },
              ]}
            />

            {/* Render OverviewTable if numbers data length is greater than 0 */}
            <OverviewTable
              heading={"Numbers"}
              total={numbers?.length}
              buttons={[
                status !== AUTO_CANCELLED && 
                <Button
                  key={1}
                  variant="outlined"
                  styleOverride={{ backgroundColor: "#fff", fontSize: "14px" }}
                  buttonText={"Manage number"}
                  onClick={() => navigate("/virtual-numbers")}
                  hierarchy="white"
                  disabled={false}
                />,
              ]}
              renewDate={endDate}
              rows={numbers?.map((elem) => ({
                key: elem,
                data: (
                  <p className="t7 regular-font">
                    <span className="medium-font">
                      {elem.total} {elem.location}{" "}
                      {elem.number_type?.toLowerCase() !== "standard"
                        ? elem.number_type?.toLowerCase()
                        : ""}
                    </span>{" "}
                    number x{" "}
                    <span
                      className="medium-font"
                      style={{
                        textDecoration:
                          Number(elem.price) !== Number(elem.discounted_price)
                            ? "line-through"
                            : "none",
                      }}
                    >
                      {currency}
                      {elem.price}
                    </span>{" "}
                    {Number(elem.price) !== Number(elem.discounted_price) && (
                      <span className="medium-font">
                        {currency}
                        {elem.discounted_price}{" "}
                      </span>
                    )}
                    per month{" "}
                    <span style={{ color: "#78807a", fontStyle: "normal" }}>
                      {elem.is_included_in_plan ? "(included with plan)" : ""}
                    </span>
                  </p>
                ),
              }))}
            />
          </section>
          <hr />
          <section>
            <h2 className="t5 medium-font">Add-ons</h2>
            {applicableAddons?.map((addon) => {
              if (addon?.name === "AI INSIGHTS") return null;
              if (addon?.name === "WHATSAPP BUSINESS")
                return (
                  <OverviewTable
                    heading={"WhatsApp Business"}
                    buttons={[
                      status !== AUTO_CANCELLED && 
                      <Button
                        key={2}
                        variant="outlined"
                        styleOverride={{
                          backgroundColor: "#fff",
                          fontSize: "14px",
                        }}
                        buttonText={
                          addon?.licenses?.total === 0
                            ? "Add license"
                            : "Manage license"
                        }
                        onClick={() =>
                          handleOpenLicenseModal(
                            sentenceCase(addon?.name),
                            addon,
                          )
                        }
                        hierarchy="white"
                        disabled={whatsappAPIStatus !== "ACTIVE"}
                      />,
                    ]}
                    customBody={
                      <div className={styles.whatsappBody}>
                        <p>
                          <span className="medium-font">
                            {addon?.licenses?.total}
                          </span>{" "}
                          {addon?.licenses?.total > 1 ? "Licenses" : "License"}{" "}
                          x{" "}
                          {addon?.price > addon?.discounted_price && (
                            <span style={{ textDecoration: "line-through" }}>
                              {currency}
                              {addon?.price}
                            </span>
                          )}{" "}
                          <span className="medium-font">
                            {currency}
                            {addon?.discounted_price}
                          </span>{" "}
                          per month
                        </p>
                        {addon?.licenses?.total > 0 && (
                          <div className={styles.whatsappRow}>
                            <span>
                              <span className="medium-font">
                                {addon?.licenses?.total - addon?.licenses?.free}
                              </span>
                              {addon?.licenses?.total > 1
                                ? " Licenses "
                                : " License "}
                              assigned
                            </span>{" "}
                            {canCreateEditUsers && (
                              <button
                                onClick={() =>
                                  setAssignUsersModal({
                                    name: addon?.name,
                                    visible: true,
                                    data: addon,
                                  })
                                }
                              >
                                View
                              </button>
                            )}
                          </div>
                        )}
                        {connectedNumbers ? (
                          <div className={styles.whatsappRow}>
                            <span>
                              {connectedNumbers}
                              {connectedNumbers > 1 ? " Numbers " : " Number "}
                              connected
                            </span>
                            <a
                              target="_blank"
                              rel="noopener noreferrer"
                              href="https://business.facebook.com/latest/whatsapp_manager/phone_numbers"
                            >
                              View
                            </a>
                          </div>
                        ) : null}
                      </div>
                    }
                    customFooter={
                      <div className={styles.whatsappFooter}>
                        <div className={styles.row1}>
                          <div>
                            <span>WhatsApp Business API</span>
                            <Tag
                              quality={
                                whatsappAPIStatus === "PENDING"
                                  ? "UNKNOWN"
                                  : whatsappAPIStatus === "FLAGGED"
                                    ? "RED"
                                    : whatsappAPIStatus === "ACTIVE"
                                      ? "GREEN"
                                      : whatsappAPIStatus === "INPROGRESS"
                                        ? "BLUE"
                                        : ""
                              }
                              name={
                                whatsappAPIStatus === "INPROGRESS"
                                  ? "In progress"
                                  : whatsappAPIStatus?.[0]?.toUpperCase() +
                                    whatsappAPIStatus?.slice(1)?.toLowerCase()
                              }
                            />
                          </div>
                          {whatsappAPIStatus === "PENDING" ? (
                            <Button
                              hierarchy="green"
                              buttonText="Apply now"
                              onClick={launchWhatsAppSignup}
                            />
                          ) : whatsappAPIStatus === "FLAGGED" ? (
                            <Button
                              hierarchy="green"
                              buttonText="Retry"
                              onClick={launchWhatsAppSignup}
                            />
                          ) : null}
                        </div>
                        {(whatsappAPIStatus === "INPROGRESS" ||
                          whatsappAPIStatus === "ACTIVE") &&
                        showPaymentMethodWarning ? (
                          <div className={styles.warningMsg}>
                            <p>
                              Please add payment method to access WhatsApp
                              assets and messaging. Dismiss if already done.
                            </p>
                            <div>
                              <a
                                target="_blank"
                                rel="noopener noreferrer"
                                href="https://business.facebook.com/wa/manage/home/"
                              >
                                Add now
                              </a>
                              <div />
                              <button
                                onClick={() =>
                                  setShowPaymentMethodWarning(false)
                                }
                              >
                                Dismiss
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    }
                  />
                );
              return (
                <OverviewTable
                  total={addon?.licenses?.total}
                  heading={sentenceCase(addon?.name)}
                  renewDate={endDate}
                  buttons={[
                    status !== AUTO_CANCELLED && 
                    <Button
                      key={1}
                      variant="outlined"
                      styleOverride={{
                        backgroundColor: "#fff",
                        fontSize: "14px",
                      }}
                      buttonText={
                        addon?.licenses?.total === 0
                          ? "Add license"
                          : "Manage license"
                      }
                      onClick={() =>
                        handleOpenLicenseModal(sentenceCase(addon?.name), addon)
                      }
                      hierarchy="white"
                      disabled={false}
                    />,
                  ]}
                  rows={[
                    {
                      data: (
                        <p>
                          <span className="medium-font">
                            {addon?.licenses?.total}
                          </span>{" "}
                          License{addon?.licenses?.total > 1 ? "s" : ""} x{" "}
                          {addon?.price > addon?.discounted_price && (
                            <span style={{ textDecoration: "line-through" }}>
                              {currency}
                              {addon?.price}
                            </span>
                          )}
                          <span className="medium-font">
                            {" "}
                            {currency}
                            {addon?.discounted_price}
                          </span>{" "}
                          per month
                        </p>
                      ),
                    },
                    {
                      data: (
                        <p>
                          <span className="medium-font">
                            {addon?.licenses?.total - addon?.licenses?.free}
                          </span>{" "}
                          License{addon?.licenses?.total > 1 ? "s" : ""}{" "}
                          assigned
                        </p>
                      ),
                      buttonText: "View",
                      btnClick: () =>
                        setAssignUsersModal({
                          name: addon?.name,
                          visible: true,
                          data: addon,
                        }),
                    },
                  ]}
                />
              );
            })}
          </section>
        </>
      )}
    </div>
  );
};
const OverviewTable = ({
  heading,
  buttons = [],
  renewDate = null,
  rows,
  total,
  customBody = null,
  customFooter = null,
}) => {
  const { canCreateEditUsers } = useSelector(
    (state) => state?.auth?.permissions
  );
  return (
    <div className={styles.table}>
      <div className={styles.header}>
        <span className="t6 medium-font">{heading}</span>
        {buttons.length > 0 && <div>{buttons?.map((btn) => btn)}</div>}
      </div>
      {customBody ? (
        customBody
      ) : (
        <div className={styles.body}>
          {rows?.slice(0, total > 0 ? rows.length : 1)?.map((row, index) => {
            return (
              <div key={index} className={styles.row}>
                <span>{row.data}</span>
                {row.buttonText && canCreateEditUsers && (
                  <button onClick={row.btnClick}>{row.buttonText}</button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {customFooter ? customFooter : null}
    </div>
  );
};
export default OverviewTab;
